import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ErrorResponse } from "./common";
import {
  Asset,
  CompleteProvideLiquidityRequest,
  CompleteProvideLiquidityActivateRequest,
  GetBalancesResponse,
  Pool,
  ProvideLiquidityRequest,
  RemoveLiquidityRequest,
  SimulateAddLiquidityRequest,
  SimulateAddLiquidityResponse,
  SwapRequest,
  SwapSimulateRequest,
  SwapSimulateResponse,
  TransactionData,
  GetPoolsResponse,
  GetAssetsResponse,
  GetBalancesSTONResponse,
} from "./dexApiTypes";
import { REACT_APP_BACKEND_URL, STON_FI_API_URL } from "constant";

const BACKEND_URL = STON_FI_API_URL;
export const dexApiV1 = createApi({
  reducerPath: "dexApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}/v1`,
  }),
  endpoints: (builder) => ({

    getAssetsSton: builder.query<{ [key: string]: Asset }, void>({
      query: (address: any) => address ? `/assets?wallet_address=${address}` : "/assets",
      
      transformResponse: (response: GetAssetsResponse) => {
        
        return response.asset_list.reduce((acc: { [key: string]: Asset }, asset) => {
          return {
            ...acc,
            [asset.contract_address]: asset,
          };
        }, {});
      },
    }),

    getWalletPoolsSton: builder.query<Pool[], string>({
      query: (wallet_address) => `/wallets/${wallet_address}/pools`,
    }),

    getPoolsSton: builder.query<Pool[], void>({
      query: () => "/pools",
      transformResponse: (response: GetPoolsResponse) => {
        return response.pool_list;
      },
    }),

    simulateSwap: builder.query<
      SwapSimulateResponse | ErrorResponse,
      SwapSimulateRequest
    >({
      query: ({
        swapAction,
        offer_address,
        ask_address,
        units,
        slippage_tolerance,
        referral_address = null,
      }) => ({
        url:
          swapAction === "offer" ? `/swap/simulate?offer_address=${offer_address}&ask_address=${ask_address}&units=${units}&slippage_tolerance=${slippage_tolerance}` : "/reverse_swap/simulate",
        method: "POST",
        body: {},
      }),
    }),

    swap: builder.query<TransactionData, SwapRequest>({
      query: ({
        userWalletAddress,
        offerJettonAddress,
        offerAmount,
        askJettonAddress,
        minAskAmount,
      }) => ({
        url: "/swap",
        method: "POST",
        body: {
          userWalletAddress,
          offerJettonAddress,
          offerAmount,
          askJettonAddress,
          minAskAmount,
        },
      }),
    }),

    simulateAddLiquiditySton: builder.query<
      SimulateAddLiquidityResponse,
      SimulateAddLiquidityRequest
    >({
      query: ({
        token0_address,
        token0_amount,
        token1_address,
        token1_amount,
        slippage_tolerance,
        user_wallet_address,
        lp_account_address,
      }) => ({
        url: "/dex/liquidity/provide/simulate",
        method: "POST",
        body: {
          token0_address,
          token0_amount,
          token1_address,
          token1_amount,
          slippage_tolerance,
          user_wallet_address:
            user_wallet_address !== "" ? user_wallet_address : null,
          lp_account_address,
        },
      }),
    }),

    provideLiquiditySton: builder.query<TransactionData, ProvideLiquidityRequest>({
      query: ({
        user_wallet_address,
        token0_address,
        token1_address,
        token0_amount,
        token1_amount,
        min_lp_out,
      }) => ({
        url: "/dex/liquidity/provide",
        method: "POST",
        body: {
          user_wallet_address,
          token0_address,
          token1_address,
          token0_amount,
          token1_amount,
          min_lp_out,
        },
      }),
    }),

    completeProvideLiquiditySton: builder.query<
      TransactionData,
      CompleteProvideLiquidityRequest
    >({
      query: ({
        user_wallet_address,
        token_address,
        second_token_address,
        token_amount,
        min_lp_out,
      }) => ({
        url: "/dex/liquidity/provide_complete",
        method: "POST",
        body: {
          user_wallet_address,
          token_address,
          second_token_address,
          token_amount,
          min_lp_out,
        },
      }),
    }),

    completeProvideLiquidityActivateSton: builder.query<
      TransactionData,
      CompleteProvideLiquidityActivateRequest
    >({
      query: ({
        token0_amount,
        token1_amount,
        min_lp_out,
        lp_account_address,
      }) => ({
        url: "/dex/liquidity/provide_complete_activate",
        method: "POST",
        body: {
          token0_amount,
          token1_amount,
          min_lp_out,
          lp_account_address,
        },
      }),
    }),

    removeLiquiditySton: builder.query<TransactionData, RemoveLiquidityRequest>({
      query: ({
        user_wallet_address,
        token0_address,
        token1_address,
        lp_tokens_amount,
      }) => ({
        url: "/dex/liquidity/remove",
        method: "POST",
        body: {
          user_wallet_address,
          token0_address,
          token1_address,
          lp_tokens_amount,
        },
      }),
    }),

    // GET METHOD
    getBalancesSton: builder.query<{ [key: string]: number }, string>({
      query: (wallet_address) => ({
        url: `/wallets/${wallet_address}/assets`,
        method: "GET",
      }),
      transformResponse: (response: GetBalancesSTONResponse) => {
        
        return response.asset_list.filter((item) => item.balance > 0).reduce((acc: { [key: string]: number }, asset) => {
          return {
            ...acc,
            [asset.contract_address]: asset.balance,
          };
        }, {});
      },

      
    }),

    getTokenInfo: builder.query({
      query: (contract) => ({
        url: `/token/get_token_info?address=${contract}`,
        method: "GET",
      }),
      transformResponse: (response: any) => {
        return response.tokenInfo;
      },
    }),
  }),
});


export const stonFiDexApiV1 = createApi({
  reducerPath: "stonDexApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${REACT_APP_BACKEND_URL}/api/v1`,
  }),
  endpoints: (builder) => ({
    getWalletPools: builder.query<Pool[], string>({
      query: (wallet_address) => `/wallet/${wallet_address}/get_pools`,
    }),
    getPools: builder.query<Pool[], void>({
      query: () => "/pools",
    }),
    getAssets: builder.query<{ [key: string]: Asset }, void>({
      query: (address: any) => address ? `/assets?wallet_address=${address}` : "/assets",
      transformResponse: (response: Asset[]) => {
        return response.reduce((acc: { [key: string]: Asset }, asset) => {
          return {
            ...acc,
            [asset.contract_address]: asset,
          };
        }, {});
      },
    }),
    getBalances: builder.query<{ [key: string]: number }, string>({
      query: (wallet_address) => ({
        url: `/wallet/${wallet_address}/get_balances`,
        method: "GET",
      }),
      transformResponse: (response: GetBalancesResponse) => {
        return response.balances;
      },
    }),
    simulateAddLiquidity: builder.query<
    SimulateAddLiquidityResponse,
    SimulateAddLiquidityRequest
  >({
    query: ({
      token0_address,
      token0_amount,
      token1_address,
      token1_amount,
      slippage_tolerance,
      user_wallet_address,
      lp_account_address,
    }) => ({
      url: "/dex/liquidity/provide/simulate",
      method: "POST",
      body: {
        token0_address,
        token0_amount,
        token1_address,
        token1_amount,
        slippage_tolerance,
        user_wallet_address:
          user_wallet_address !== "" ? user_wallet_address : null,
        lp_account_address,
      },
    }),
  }),

  provideLiquidity: builder.query<TransactionData, ProvideLiquidityRequest>({
    query: ({
      user_wallet_address,
      token0_address,
      token1_address,
      token0_amount,
      token1_amount,
      min_lp_out,
    }) => ({
      url: "/dex/liquidity/provide",
      method: "POST",
      body: {
        user_wallet_address,
        token0_address,
        token1_address,
        token0_amount,
        token1_amount,
        min_lp_out,
      },
    }),
  }),

  completeProvideLiquidity: builder.query<
    TransactionData,
    CompleteProvideLiquidityRequest
  >({
    query: ({
      user_wallet_address,
      token_address,
      second_token_address,
      token_amount,
      min_lp_out,
    }) => ({
      url: "/dex/liquidity/provide_complete",
      method: "POST",
      body: {
        user_wallet_address,
        token_address,
        second_token_address,
        token_amount,
        min_lp_out,
      },
    }),
  }),

  completeProvideLiquidityActivate: builder.query<
    TransactionData,
    CompleteProvideLiquidityActivateRequest
  >({
    query: ({
      token0_amount,
      token1_amount,
      min_lp_out,
      lp_account_address,
    }) => ({
      url: "/dex/liquidity/provide_complete_activate",
      method: "POST",
      body: {
        token0_amount,
        token1_amount,
        min_lp_out,
        lp_account_address,
      },
    }),
  }),

  removeLiquidity: builder.query<TransactionData, RemoveLiquidityRequest>({
    query: ({
      user_wallet_address,
      token0_address,
      token1_address,
      lp_tokens_amount,
    }) => ({
      url: "/dex/liquidity/remove",
      method: "POST",
      body: {
        user_wallet_address,
        token0_address,
        token1_address,
        lp_tokens_amount,
      },
    }),
  }),

  })
})


export const {
  useGetAssetsStonQuery,
  useGetWalletPoolsStonQuery,
  useGetPoolsStonQuery,
  useSimulateSwapQuery,
  useSwapQuery,
  useGetBalancesStonQuery,
  useGetTokenInfoQuery,
  useSimulateAddLiquidityStonQuery,
  useProvideLiquidityStonQuery,
  useCompleteProvideLiquidityStonQuery,
  useCompleteProvideLiquidityActivateStonQuery,
  useRemoveLiquidityStonQuery,
} = dexApiV1;

export const {
  useGetWalletPoolsQuery,
  useGetAssetsQuery,
  useGetPoolsQuery,
  useGetBalancesQuery,
  useSimulateAddLiquidityQuery,
  useProvideLiquidityQuery,
  useCompleteProvideLiquidityQuery,
  useCompleteProvideLiquidityActivateQuery,
  useRemoveLiquidityQuery,
} = stonFiDexApiV1